import React, { useState, useEffect } from 'react';
import { isDateAvailable, DAY_NAMES } from '../utils/bookingHelpers';

const DatePicker = ({ selectedDate, onDateSelect, location, isFromRepair = false }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  useEffect(() => {
    // Reset to current month when location changes
    setCurrentMonth(new Date());
  }, [location]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Can't select past dates
    if (date < today) return false;
    
    // Must be available day for location
    if (!location) return false;
    
    return isDateAvailable(date, location, isFromRepair);
  };

  const isDateSelected = (date) => {
    if (!date || !selectedDate) return false;
    // Compare date strings directly to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return dateString === selectedDate;
  };

  const handleDateClick = (date) => {
    if (isDateSelectable(date)) {
      // Format date in local timezone to avoid timezone shifting
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onDateSelect(`${year}-${month}-${day}`);
    }
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  const canGoPrevious = 
    currentMonth.getFullYear() > today.getFullYear() ||
    (currentMonth.getFullYear() === today.getFullYear() && currentMonth.getMonth() > today.getMonth());

  return (
    <div className="w-full max-w-sm mx-auto">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          disabled={!canGoPrevious}
          className={`p-2 rounded-lg transition-colors duration-200 ${
            canGoPrevious 
              ? 'text-gold-600 hover:bg-gold-50' 
              : 'text-gray-300 cursor-not-allowed'
          }`}
        >
          ←
        </button>
        
        <h3 className="font-playfair text-lg text-charcoal">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="p-2 rounded-lg text-gold-600 hover:bg-gold-50 transition-colors duration-200"
        >
          →
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAY_NAMES.map(day => (
          <div key={day} className="text-center text-xs font-montserrat text-charcoal/60 py-2">
            {day.slice(0, 3)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-10" />;
          }

          const selectable = isDateSelectable(date);
          const selected = isDateSelected(date);
          const isToday = date.toDateString() === today.toDateString();

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDateClick(date)}
              disabled={!selectable}
              className={`
                h-10 w-10 rounded-lg text-sm font-montserrat transition-all duration-200
                ${selected 
                  ? 'bg-gold-500 text-white ring-2 ring-gold-300' 
                  : selectable
                    ? 'text-charcoal hover:bg-gold-100 border border-transparent hover:border-gold-300'
                    : 'text-gray-300 cursor-not-allowed'
                }
                ${isToday && !selected ? 'border border-gold-400' : ''}
              `}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      {/* Location-specific availability note */}
      {location && (
        <div className="mt-4 p-3 bg-gold-50 rounded-lg">
          <p className="text-xs font-montserrat text-charcoal/70 text-center">
            {isFromRepair ? (
              location === 'Alpharetta' ? (
                'Repair appointments: Monday, Wednesday, Friday only'
              ) : (
                'Repair appointments: Sunday, Thursday, Saturday only'
              )
            ) : (
              'General appointments available 7 days a week'
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default DatePicker;